import React, { memo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Checkbox, Group, LoadingOverlay, Stack, Table, Text,
} from '@mantine/core';
import { RotatingLines } from 'react-loader-spinner';

import { NoteWithoutAct, NotesWithoutActState } from 'types/mes/NotesWithoutActTypes';
import { RootState } from 'redux/reducers/combineReducers';
import { changeSelectedNoteId } from 'redux/reducers/mes/notesWithoutActReducer';
import { IAuthResponseType } from 'redux/store/configureStore';
import { ReactComponent as EditIcon } from 'assets/icons/edit-icon.svg';

import classes from './styles.module.css';
import headCells from './config';

interface NotesWithoutActsTableProps {
  selectedNotesId: number[];
  handleSelectNote: (newValue: number[]) => void;
  noteModalOpen: () => void;
  changeIsEditModal: (value: boolean) => void;
}

function NotesWithoutActsTable(props: NotesWithoutActsTableProps) {
  const {
    selectedNotesId,
    handleSelectNote,
    noteModalOpen,
    changeIsEditModal,
  } = props;

  const {
    requestStatus,
    notesWithoutAct,
  } = useSelector<RootState, NotesWithoutActState>(
    (state) => state.notesWithoutAct,
  );

  const dispatch: IAuthResponseType = useDispatch();

  const handleClick = (id: number, isChecked: boolean) => {
    const newSelected = isChecked
      ? [...selectedNotesId, id]
      : selectedNotesId.filter((el) => el !== id);

    handleSelectNote(newSelected);
  };

  const isSelected = (id: number) => selectedNotesId.indexOf(id) !== -1;

  const handleEditIconClick = (id: number) => {
    dispatch(changeSelectedNoteId(id));
    changeIsEditModal(true);
    noteModalOpen();
  };

  const isIncompleteMobileNote = (row: NoteWithoutAct) => (
    !row.street || !row.houseNumber
  );

  return (
    <div className="notes-table">
      <Stack w="100%" gap={10}>
        {notesWithoutAct.length > 0 && (
          <Text size="sm" ta="right" c="dimmed">
            Количество заявок:
            {' '}
            {notesWithoutAct.length}
          </Text>
        )}
        <Group w="100%">
          <Table
            striped
            highlightOnHover
            withTableBorder
            classNames={{
              th: classes.tableHeadCell,
              td: classes.tableBodyCell,
              tr: classes.tableRow,
            }}
          >
            <Table.Thead>
              <Table.Tr>
                {headCells.map((headCell) => (
                  <Table.Th key={headCell.id}>{headCell.label}</Table.Th>
                ))}
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {notesWithoutAct.length !== 0 && notesWithoutAct.map((row) => {
                const isItemSelected = isSelected(row.id);
                return (
                  <Table.Tr
                    key={row.id}
                    bg={isItemSelected ? 'var(--mantine-color-blue-light)' : undefined}
                  >
                    <Table.Td>
                      <Checkbox
                        checked={isItemSelected}
                        onChange={(event) => handleClick(row.id, event.currentTarget.checked)}
                      />
                    </Table.Td>
                    <Table.Td>
                      {row.street && (
                        <>
                          {row.street}
                          ,&nbsp;
                        </>
                      )}
                      {row.houseNumber && (
                        <>
                          д.&nbsp;
                          {row.houseNumber}
                        </>
                      )}
                      {row.entrance !== 0 && (
                        <>
                          ,&nbsp;
                          п.&nbsp;
                          {row.entrance}
                        </>
                      )}
                    </Table.Td>
                    <Table.Td miw={200}>{row.date.replace('T', ' ')}</Table.Td>
                    <Table.Td>{row.tel}</Table.Td>
                    <Table.Td>{row.comment}</Table.Td>
                    <Table.Td width="30px">
                      {isIncompleteMobileNote(row) && (
                        <EditIcon
                          width={20}
                          height={20}
                          style={{ cursor: 'pointer' }}
                          onClick={() => handleEditIconClick(row.id)}
                        />
                      )}
                    </Table.Td>
                  </Table.Tr>
                );
              })}
            </Table.Tbody>
          </Table>

          {notesWithoutAct.length === 0
            && requestStatus === 'fulfilled' && (
              <Stack align="center" justify="center" w="100%">
                <Text style={{ fontSize: 18, color: 'red' }}>
                  Совпадений не найдено
                </Text>
              </Stack>
          )}
        </Group>
      </Stack>

      {notesWithoutAct.length === 0 && (requestStatus === 'pending' || requestStatus === '') && (
        <LoadingOverlay
          visible
          loaderProps={{
            children: (
              <RotatingLines
                strokeColor="blue"
                strokeWidth="5"
                animationDuration="0.5"
                width="80"
                visible
              />
            ),
          }}
        />
      )}
    </div>
  );
}

export default memo(NotesWithoutActsTable);
